import NavLink from "@components/DashboardLayout/NavLink";
import React from "react";
import {
  Box,
  Button,
  HStack,
  Heading,
  Img,
  ListItem,
  Stack,
  Text,
  UnorderedList,
  useColorModeValue,
} from "@chakra-ui/react";
import { CONTAINER_PADDING_X, useUser } from "@utils";
import { FiArrowRight } from "react-icons/fi";
import { FiBookOpen } from "@react-icons/all-files/fi/FiBookOpen";
import { FiFileText } from "@react-icons/all-files/fi/FiFileText";
import { Footer } from "@components/Footer";
import { Link as GatsbyLink, PageProps, graphql, withPrefix } from "gatsby";
import { Header } from "@components/Header";
import { MarkdownRenderer } from "@components/MarkdownRenderer";
import { PageSeo } from "@components/PageSeo";
import { ShieldAnimation } from "@components/ShieldAnimation";

export default function Certification({
  location,
  data: { certification },
}: PageProps<Queries.GetCertificationDetailsQuery>) {
  if (!certification) {
    throw new Error("Unable to locate certification");
  }

  return (
    <CertificationInformationContainer
      uri={location.pathname}
      certification={certification}
    >
      <Heading as="h3" fontSize="3xl">
        Level
      </Heading>
      <Text>Apollo {certification.title}</Text>
      <Heading as="h3" fontSize="3xl" mt={6}>
        Prerequisites
      </Heading>
      <UnorderedList ml={10}>
        {certification.prerequisites.map((prerequisite) => (
          <ListItem key={prerequisite} mt={2}>
            <MarkdownRenderer>{prerequisite}</MarkdownRenderer>
          </ListItem>
        ))}
      </UnorderedList>
      <Heading as="h3" fontSize="3xl" mt={6}>
        Skills Assessed
      </Heading>
      <UnorderedList ml={10}>
        {certification.skillsAssessed.map(({ skill, tasks }) => (
          <React.Fragment key={skill}>
            <ListItem mt={4}>
              <MarkdownRenderer>{skill}</MarkdownRenderer>
            </ListItem>
            <UnorderedList ml={10}>
              {tasks.map((task) => (
                <ListItem key={task} mt={2}>
                  <MarkdownRenderer>{task}</MarkdownRenderer>
                </ListItem>
              ))}
            </UnorderedList>
          </React.Fragment>
        ))}
      </UnorderedList>
    </CertificationInformationContainer>
  );
}

export const CertificationInformationContainer: React.FC<{
  uri: string;
  certification: {
    exam: Pick<Queries.Exam, "slug"> | null;
    courses: ReadonlyArray<Pick<Queries.Course, "slug">>;
  } & Pick<
    Queries.Certification,
    "title" | "tagline" | "description" | "slug" | "image"
  >;
}> = ({ certification, children, uri }) => {
  const { title, tagline, description, courses, exam, slug, image } =
    certification;
  const heroBgColor = useColorModeValue("silver.200", "black.200");
  const baseSlug = `/certifications/${slug}`;
  const learningPathSlug = `${baseSlug}/learning-path`;

  const { user } = useUser();

  let cert = null;

  // associate cert has 2 versions, use latest version
  if (certification.slug === "apollo-graph-associate") {
    cert = user?.certifications.find(
      (cert) => cert.certificationId === "apollo-graph-associate/v2"
    );
  } else {
    cert = user?.certifications.find(
      (cert) => cert.certificationId === certification.slug
    );
  }

  return (
    <>
      <PageSeo title={title} description={description} tagline={tagline} />
      <Header>Certifications</Header>
      <Box as="main" bgColor={heroBgColor}>
        <Stack
          align="center"
          direction={{ base: "column-reverse", md: "row" }}
          py={{ base: 8, md: 12 }}
          spacing={{ base: 4, md: 10 }}
          px={CONTAINER_PADDING_X}
          maxW="container.lg"
          mx="auto"
        >
          <div>
            <Heading mb="2" size="xl">
              Apollo {title} Certification
            </Heading>
            <Text mb={{ base: 6, md: 8 }} fontSize="lg">
              {description}
            </Text>
            <Box
              fontFamily="mono"
              fontWeight="semibold"
              fontSize="sm"
              textTransform="uppercase"
              mb="2"
            >
              {cert ? "You're certified 🎉" : "Get certified"}
            </Box>
            {cert ? (
              <Button
                rightIcon={<FiArrowRight />}
                as="a"
                borderRadius={"full"}
                href={withPrefix(`/certifications/${cert.id}`)}
              >
                View certificate
              </Button>
            ) : (
              <Stack
                spacing="3"
                align="flex-start"
                direction={{ base: "column", md: "row" }}
              >
                <Button
                  leftIcon={<FiBookOpen />}
                  as={GatsbyLink}
                  borderRadius={"full"}
                  to={"/" + courses[0].slug}
                  colorScheme="blue"
                >
                  Complete the tutorials
                </Button>
                {exam && (
                  <Button
                    leftIcon={<FiFileText />}
                    as={GatsbyLink}
                    to={"/exams/" + exam.slug}
                  >
                    Take the exam
                  </Button>
                )}
              </Stack>
            )}
          </div>
          <ShieldAnimation
            as={Img}
            maxTilt={30}
            boxSize={168}
            alt={`${title} badge`}
            src={image}
          />
        </Stack>
      </Box>
      <Box
        px={CONTAINER_PADDING_X}
        pt="8"
        pb="14"
        maxW="container.lg"
        mx="auto"
      >
        <HStack
          as="nav"
          justifyContent="center"
          spacing={[8, 10, 12]}
          borderBottomWidth="1px"
          mb="8"
        >
          <NavLink to={baseSlug} uri={uri}>
            Details
          </NavLink>
          <NavLink to={learningPathSlug} uri={uri}>
            {exam ? "Paths to Certification" : "Path to Certification"}
          </NavLink>
        </HStack>
        {children}
      </Box>
      <Footer />
    </>
  );
};

export const query = graphql`
  query GetCertificationDetails($id: String!) {
    certification(id: { eq: $id }) {
      id
      slug
      title
      description
      tagline
      criteria
      image
      prerequisites
      skillsAssessed {
        skill
        tasks
      }
      exam {
        id
        slug
      }
      courses {
        id
        slug
      }
    }
  }
`;
